import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { SectionLoadingState } from '../../../components'
import {
  BACK_END_PULL_INTERVAL,
  ConformityCheckFunctionType,
  RiskReliefSmartFunctionType,
  workspaceTypes,
} from '../../../constants/constants'
import { useMediaQuery, useWorkspaceSmartFunctionData } from '../../../hooks'
import useWorkspaceDetails from '../../../hooks/useWorkspaceDetails'
import useWorkspaces from '../../../hooks/useWorkspaces'
import {
  AlgorithmRun,
  clearSelectedAlgorithmRun,
} from '../../../store/reducers/workspaceReducer'
import { AlgorithmRunStatus } from '../../../types/generalTypes'
import { routePaths } from '../../RootPage'
import { SmartFunctionPanelTableHeader } from '../components/SmartFunctionPanelTableHeader'
import SmartFunctionPanelTableRow from '../components/SmartFunctionPanelTableRow'
import { compensationMatrixAnalysisPanels } from '../../SmartFunctions/CompensationMatrixAnalysisSmartFunctionDetailsPage/Components/CompensationMatrixAnalysisSelectPanel'
import { flowCytometryReviewAnalysisPanels } from '../../SmartFunctions/FlowCytometryReviewAnalysisSmartFunctionDetailsPage/components/FlowCytometryReviewAnalysisSelectPanel'
import { smartFunctionViewTypes } from '../../SmartFunctions/components/SmartFunctionViewTypeDropdown'

const ListPanelLayout = styled.div`
  height: calc(100vh - 124px);
  border-radius: 16px 16px 0px 0px;
`

const SectionLoadingStateContainer = styled.div`
  display: flex;
  height: calc(100vh - 124px);
`

const TableHeaderContainer = styled.div`
  margin-bottom: 0;
`

const TableContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: calc(100vh - 155px);
  overflow-y: auto;
  overflow-x: ${(props) => (props.$isMobile ? 'max-content' : '100%')};
`

const TableSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-radius: 4px;
  background-color: var(--color-bg-mask);
  position: relative;
  z-index: 5;
  table {
    tr:not(:last-child) td {
      margin-bottom: 4px;
      color: blue;
    }
  }
`

export interface ExtendedAlgorithmRun extends AlgorithmRun {
  key: string
  calculatedRecordId?: string
  smartFunctionName?: string
  algorithmRunIndex?: number
  calculatedStatus?: AlgorithmRunStatus
  smartFunctionType?: RiskReliefSmartFunctionType | ConformityCheckFunctionType
  disabled?: boolean
  isStatusOngoing?: boolean
  templateName?: string
  iterationFilename?: string
  isTitleRow?: boolean
  isChildRow?: boolean
}

interface WorkspaceSmartFunctionPanelProps {
  singleWorkspace?: boolean
}

const WorkspaceSmartFunctionPanel = ({
  singleWorkspace,
}: WorkspaceSmartFunctionPanelProps) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { workspaceId, deviationId, documentId } = useParams()
  const { selectedWorkspace, refreshWorkspaceDetails } = useWorkspaceDetails({
    workspaceId,
    preventFetch: true,
  })
  const {
    algorithmRunBase,
    riskReliefSmartFunctions,
    conformityCheckSmartFunctions,
    flowCytometrySmartFunctions,
    isStatusOngoing,
    getCalculatedRecordId,
  } = useWorkspaceSmartFunctionData()
  const isMobile = useMediaQuery('xs')
  const { loading } = useWorkspaces({ preventFetch: true })

  const formattedData: ExtendedAlgorithmRun[][] = useMemo(() => {
    const defaultRun: ExtendedAlgorithmRun = algorithmRunBase

    switch (selectedWorkspace?.typeCode) {
      case workspaceTypes.RISK_REL:
        return riskReliefSmartFunctions
      case workspaceTypes.OFR_QR:
        return conformityCheckSmartFunctions
      case workspaceTypes.CC_FC:
        return flowCytometrySmartFunctions
      default: {
        const runs = selectedWorkspace?.algorithmRuns
        return !!runs && runs.length > 0
          ? runs.map((run: AlgorithmRun, index: number) => [
              {
                ...run,
                key: run.id,
                calculatedRecordId: getCalculatedRecordId(run.id),
                smartFunctionName: selectedWorkspace?.algorithmName ?? '',
                algorithmRunIndex: index + 1,
                calculatedStatus: run?.runStatus,
                isStatusOngoing: selectedWorkspace?.algorithmRuns
                  ? isStatusOngoing(selectedWorkspace?.algorithmRuns)
                  : false,
              } as ExtendedAlgorithmRun,
            ])
          : [[defaultRun]]
      }
    }
  }, [
    selectedWorkspace,
    isStatusOngoing,
    algorithmRunBase,
    riskReliefSmartFunctions,
    conformityCheckSmartFunctions,
    flowCytometrySmartFunctions,
    getCalculatedRecordId,
  ])

  useEffect(() => {
    const interval = setInterval(() => {
      refreshWorkspaceDetails()
    }, BACK_END_PULL_INTERVAL)

    return () => clearInterval(interval) // Clear the interval to prevent memory leaks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRecordClick = useCallback(
    (record: ExtendedAlgorithmRun) => {
      if (record.key !== '0' && !record.disabled) {
        dispatch(clearSelectedAlgorithmRun())
        let baseRoute = ''
        let extra = ''
        switch (selectedWorkspace?.typeCode) {
          case workspaceTypes.BIOMARK:
            baseRoute = routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_BIOMARK
            break
          case workspaceTypes.REFAI:
            baseRoute = singleWorkspace
              ? routePaths.SINGLE_WORKSPACE_SMART_FUNCTION_DETAILS_REFAI
              : routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_REFAI
            break
          case workspaceTypes.REGSUB:
            baseRoute = routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_REGSUB
            break
          case workspaceTypes.DEL:
            baseRoute = routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_DEL
            break
          case workspaceTypes.OFR_QR:
            baseRoute = routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_OFR_QR
            extra = `/${documentId}`
            break
          case workspaceTypes.RISK_REL:
            switch (record.key) {
              case '1':
                baseRoute =
                  routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_RISK_REL_DEVIATION_CLASSIFICATION
                extra = `/${deviationId}`
                break
              case '2':
                baseRoute =
                  routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_RISK_REL_ROOT_CAUSE_DISCOVERY
                extra = `/${deviationId}`
                break
              case '3':
                baseRoute =
                  routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_RISK_REL_CAPA_GENERATION
                extra = `/${deviationId}`
                break
            }
            break
          case workspaceTypes.CC_MCC:
            baseRoute = routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_CC_MCC
            break
          case workspaceTypes.CC_FC:
            switch (record.key) {
              case '10':
                baseRoute =
                  routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_CC_FC_COMPENSATION_MATRIX_ANALYSIS
                extra = `/${compensationMatrixAnalysisPanels.MATCHING}`
                break
              case '11':
                baseRoute =
                  routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_CC_FC_COMPENSATION_MATRIX_ANALYSIS
                extra = `/${compensationMatrixAnalysisPanels.FILTERING}`
                break
              case '2':
                baseRoute =
                  routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_CC_FC_REVIEW_ANALYSIS
                extra = `/${smartFunctionViewTypes.TABLE}/${flowCytometryReviewAnalysisPanels.GATE_REVIEW}`
                break
            }
            break
        }

        navigate(
          `${baseRoute}/${selectedWorkspace?.id}/${record.calculatedRecordId}${extra}`,
        )
      }
    },
    [
      selectedWorkspace,
      deviationId,
      documentId,
      dispatch,
      navigate,
      singleWorkspace,
    ],
  )

  return loading ? (
    <SectionLoadingStateContainer>
      <SectionLoadingState />
    </SectionLoadingStateContainer>
  ) : (
    <ListPanelLayout>
      <TableHeaderContainer>
        <SmartFunctionPanelTableHeader />
      </TableHeaderContainer>
      <TableContainer $isMobile={isMobile}>
        {formattedData.map((tableData, index) => (
          <TableSectionContainer>
            {tableData.map((tableRow) => (
              <SmartFunctionPanelTableRow
                record={tableRow}
                onClick={() => {
                  handleRecordClick(tableRow)
                }}
              />
            ))}
          </TableSectionContainer>
        ))}
      </TableContainer>
    </ListPanelLayout>
  )
}

export default WorkspaceSmartFunctionPanel
